<template>
  <div class="layout-container" :class="{ 'isMenuOpen': isMenuOpen }">
    <!-- 导航栏 -->
    <header class="header" :class="{ 'header-scrolled': isScrolled }">
      <div class="header-content">
        <router-link to="/" class="logo">
          <img src="@/assets/logo.jpg" alt="行序科技">
          <span class="company-name">行序科技</span>
        </router-link>

        <!-- 桌面端导航菜单 -->
        <nav class="nav-menu" :class="{ 'active': isMenuOpen }">
          <router-link to="/" class="nav-item" @click="closeMenu">首页</router-link>
          <router-link to="/products" class="nav-item" @click="closeMenu">产品中心</router-link>
          <router-link to="/technology" class="nav-item" @click="closeMenu">技术中心</router-link>
          <router-link to="/news" class="nav-item" @click="closeMenu">新闻资讯</router-link>
          <router-link to="/about" class="nav-item" @click="closeMenu">关于我们</router-link>
          <router-link to="/contact" class="nav-item" @click="closeMenu">联系我们</router-link>
        </nav>

        <!-- 语言切换 -->
        <div class="language-switch">
          <el-dropdown trigger="click" @command="handleLanguageChange">
            <span class="language-text">
              {{ currentLanguage === 'zh' ? '中文' : 'English' }}
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="zh">中文</el-dropdown-item>
                <el-dropdown-item command="en">English</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <!-- 移动端菜单按钮 -->
        <div class="menu-toggle" @click="toggleMenu">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </header>

    <!-- 主要内容区 -->
    <main class="main-content">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h3>联系我们</h3>
          <p>地址：{{ contactInfo.address }}</p>
          <p>电话：{{ contactInfo.phone }}</p>
          <p>邮箱：{{ contactInfo.email }}</p>
        </div>
        <div class="footer-section">
          <h3>快速链接</h3>
          <router-link to="/products">产品中心</router-link>
          <router-link to="/technology">技术中心</router-link>
          <router-link to="/news">新闻资讯</router-link>
          <router-link to="/about">关于我们</router-link>
          <router-link to="/contact">联系我们</router-link>
        </div>
        <div class="footer-section">
          <h3>关注我们</h3>
          <div class="social-links">
            <a href="#" target="_blank"><i class="el-icon-weixin"></i></a>
            <a href="#" target="_blank"><i class="el-icon-weibo"></i></a>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; {{ currentYear }} 北京行序科技有限公司 版权所有</p>
        <!-- 备案信息 -->
        <div class="filing-info">
          <div class="filing-item">
            <span class="filing-label">主办单位：</span>
            <span class="filing-text">北京行序科技有限公司</span>
          </div>
          <div class="filing-item">
            <span class="filing-label">备案号：</span>
            <a href="https://beian.miit.gov.cn/" target="_blank" class="filing-link">
              京ICP备2025126586号
            </a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted, onUnmounted } from 'vue'
import { ArrowDown } from '@element-plus/icons-vue'
import { fetchContentByCode } from '@/utils/cms'

export default {
  name: 'NewLayout',
  components: {
    ArrowDown
  },
  setup() {
    const isScrolled = ref(false)
    const isMenuOpen = ref(false)
    const currentLanguage = ref('zh')
    const contactInfo = reactive({
      address: '北京市昌平区鼓楼南街6号8层',
      phone: '15711100050',
      email: 'info@xingxutech.com'
    })

    const currentYear = computed(() => new Date().getFullYear())

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 50
    }

    const toggleMenu = () => {
      isMenuOpen.value = !isMenuOpen.value
      document.body.style.overflow = isMenuOpen.value ? 'hidden' : ''
    }

    const handleLanguageChange = (lang) => {
      currentLanguage.value = lang
      // 这里可以添加语言切换的具体实现
    }

    const closeMenu = () => {
      isMenuOpen.value = false
      document.body.style.overflow = 'auto'
    }

    onMounted(async () => {
      window.addEventListener('scroll', handleScroll)
      const address = await fetchContentByCode('contact_address')
      const phone = await fetchContentByCode('contact_phone')
      const email = await fetchContentByCode('contact_email')
      if (address?.content) contactInfo.address = address.content
      if (phone?.content) contactInfo.phone = phone.content
      if (email?.content) contactInfo.email = email.content
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      isScrolled,
      isMenuOpen,
      currentLanguage,
      currentYear,
      toggleMenu,
      handleLanguageChange,
      closeMenu,
      contactInfo
    }
  }
}
</script>

<style scoped>
.layout-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 导航栏样式 */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header-scrolled {
  height: 60px;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
}

.logo img {
  height: 40px;
  margin-right: 10px;
}

.company-name {
  font-size: 20px;
  font-weight: 600;
}

.nav-menu {
  display: flex;
  gap: 30px;
}

.nav-item {
  text-decoration: none;
  color: #333;
  font-size: 16px;
  position: relative;
  padding: 5px 0;
  font-weight: 500;
}

.nav-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: #1890ff;
  transition: width 0.3s ease;
}

.nav-item:hover::after,
.nav-item.router-link-active::after {
  width: 100%;
}

.language-switch {
  margin-left: 30px;
}

.language-text {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* 主要内容区样式 */
.main-content {
  flex: 1;
  margin-top: 80px;
}

/* 页脚样式 */
.footer {
  background: #2c3e50;
  color: white;
  padding: 60px 0 20px;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
}

.footer-section h3 {
  font-size: 18px;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
}

.footer-section h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 30px;
  height: 2px;
  background: #1890ff;
}

.footer-section a {
  display: block;
  color: #fff;
  text-decoration: none;
  margin-bottom: 10px;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.footer-section a:hover {
  opacity: 1;
}

.social-links {
  display: flex;
  gap: 20px;
}

.social-links a {
  font-size: 24px;
}

.footer-bottom {
  text-align: center;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* 备案信息样式 */
.filing-info {
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;
}

.filing-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.filing-label {
  margin-right: 5px;
  font-weight: 500;
}

.filing-text {
  color: rgba(255, 255, 255, 0.7);
}

.filing-link {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
}

.filing-link:hover {
  color: #1890ff;
  border-bottom-color: #1890ff;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 移动端菜单按钮 */
.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 20px;
  cursor: pointer;
}

.menu-toggle span {
  display: block;
  width: 100%;
  height: 2px;
  background: #333;
  transition: all 0.3s ease;
}

/* 菜单打开时的按钮样式 */
.isMenuOpen .menu-toggle span:nth-child(1) {
  transform: translateY(9px) rotate(45deg);
}

.isMenuOpen .menu-toggle span:nth-child(2) {
  opacity: 0;
}

.isMenuOpen .menu-toggle span:nth-child(3) {
  transform: translateY(-9px) rotate(-45deg);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .header-content {
    padding: 0 20px;
  }

  .menu-toggle {
    display: flex;
  }

  .nav-menu {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    z-index: 1000;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    background-color: rgba(255, 255, 255, 0.05);
  }

  .nav-menu.active {
    transform: translateX(0);
  }

  .nav-menu .nav-item {
    padding: 15px 20px;
    font-size: 18px;
    display: block;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    color: #333;
    text-decoration: none;
    transition: background-color 0.2s ease;
  }

  .nav-menu .nav-item:hover {
    background-color: rgba(255, 255, 255, 0.7);
  }

  .nav-menu .nav-item.router-link-active {
    background-color: rgba(255, 255, 255, 0.8);
    font-weight: bold;
  }

  .language-switch {
    margin-left: 0;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 0 20px;
  }

  /* 移动端备案信息样式 */
  .filing-info {
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
  }

  .filing-item {
    font-size: 13px;
    justify-content: center;
  }
}
</style> 